/*
 *   File : dashboard.js
 *   Author : https://evoqins.com
 *   Description :Home page of rayi
 *   Integrations : 'js-cookie , react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { id } from 'date-fns/locale';

// custom components
import { BarChart } from '../../Components/Chart';
import AreaChart from '../../Components/Chart/area';
import { StatisticCard } from '../../Components/Card';
import { CustomTab, EmptyScreen } from '../../Components/Others';
import { CustomDataTable } from '../../Components/DataTable';
import { TextButton } from '../../Components/Buttons';
import { CustomSelectBox } from '../../Components/FormElements';
import { APIService } from '../../Service';

// custom style
import style from "../../Style/Component/order.module.scss";
import Store from '../../Store/store';

const OPTIONS = [
    {
        value: 1,
        label: 'Weekly',
    },
    {
        value: 2,
        label: 'Monthly',
    },
    {
        value: 3,
        label: 'Yearly',
    }
];

const TAB_TITLES = [
    { id: 1, label: 'AUM' },
    { id: 2, label: 'Transactions' },
    { id: 3, label: 'Users' }
];

const Dashboard = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const isRendered = useRef(true);
    const isGraphData = useRef(true);

    const [selectedOptionsAum, setSelectedOptionsAum] = useState(OPTIONS[1]);
    const [selectedOptionsUser, setSelectedOptionsUser] = useState(OPTIONS[1]);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState(0);

    const [loader, setLoader] = useState(true);
    const [rankHolderLoader, setRankHolderLoader] = useState(true);
    const [customerAumLoader, setCustomerAumLoader] = useState(true);
    const [timelineModal, setTimelineModal] = useState(false);
    const [graphLoaderAum, setGraphLoaderAum] = useState(true);
    const [graphLoaderUser, setGraphLoaderUser] = useState(true);


    const [cumulativeFrequencyDataAum, setCumulativeFrequencyDataAum] = useState({});
    const [cumulativeFrequencyDataUser, setCumulativeFrequencyDataUser] = useState({});
    const [customAumData, setCustomAumData] = useState([]);
    const [topRankers, setTopRankers] = useState([]);
    const [overviewData, setOverviewData] = useState({});

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    // custom aum , top rankers
    useEffect(() => {
        if (isRendered.current === true) {
            _getCustomerAum();
            _getTopRankers();
            _getOverviewData();
        }
        isRendered.current = false;
    }, []);

    useEffect(() => {
        if (isGraphData.current === true) {
            if (selectedOptionsAum) {
                _cumulativeFrequencyAum();
            }
            if (selectedOptionsUser) {
                _cumulativeFrequencyUser();
            }
        }
        isGraphData.current = false;
    }, [selectedOptionsAum, selectedOptionsUser])

    useEffect(() => {
        if (state !== null) {
            setSelectedTab(state.tab_index);
        }
        else {
            setSelectedTab(1);
        }
    }, [state]);

    // handle navigate
    const _handleNavigate = (id) => {
        navigate(`/customers/${id}`)
    };


    // handle tab
    const _handleTab = (tab_id) => {
        navigate('/', {
            state: {
                tab_index: tab_id
            }
        })
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setCurrentPage(page_num);
    };

    // handle state
    const _handleState = (selected) => {
        isGraphData.current = true;
        setGraphLoaderAum(true);
        setSelectedOptionsAum(selected);
    };

    // handle state user
    const _handleStateUser = (selected) => {
        isGraphData.current = true;
        setGraphLoaderUser(true);
        setSelectedOptionsUser(selected);
    };

    // handle view all
    const _handleViewAll = () => {
        navigate('/customers')
    };

    // Pending request data table column 
    const ORDER_TABLE_COLUMN = [
        {
            name: 'Customer ID',
            cell: (row) =>
                <p className='mb-0'>
                    {row.rayi_customer_id}
                </p>
            ,
            width: "120px"
        },
        {
            name: 'Name',
            cell: (row) =>
                <span className='e-text-blue-violet text-decoration-hover-underline'
                    onClick={() => _handleNavigate(row.customer_id)}>
                    {row.name}
                </span>,
            width: "250px"
        },
        {
            name: 'Email',
            cell: (row) =>
                <a href={`mailto:${row.email}`} target='_blank' className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.email}
                </a>,
            width: "260px"
        },
        {
            name: 'Phone number',
            cell: (row) =>
                <a href={`tel:${row.mobile}`} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.mobile}
                </a>,
            width: "160px"
        },
        {
            name: 'Joined date',
            selector: row => row.joined_on,
            width: "160px"
        },
        {
            name: 'AUM',
            selector: row => row.aum_text,
            width: "160px"
        }

    ];
    // Pending request data table column 
    const RANK_TABLE_COLUMN = [
        {
            name: 'Rank',
            selector: (row) => row.rank || "-",
            width: "80px"
        },
        {
            name: 'Customer ID',
            cell: (row) =>
                <p className='mb-0'>
                    {row.rayi_customer_id}
                </p>
            ,
            width: "140px"
        },
        {
            name: 'Name',
            cell: (row) =>
                <span className='e-text-blue-violet text-decoration-hover-underline'
                    onClick={() => _handleNavigate(row.customer_id)} >
                    {row.name}
                </span>,
            width: "360px"
        },
        {
            name: 'P&L',
            cell: (row) =>
                <span>
                    {parseFloat(row.p_and_l).toFixed(2)}%
                </span>,
            width: "140px"
        }
    ];


    // API - get customer aum
    const _getCustomerAum = () => {
        const is_post = false;
        const url = 'dashboard/get-customer-aum';
        APIService(is_post, url).then((response) => {

            if (response.status_code === 200) {
                setCustomAumData(response.data)
            } else {
                setCustomAumData([]);
            }
            setCustomerAumLoader(false);
        })
    };

    // API - get top rankers
    const _getTopRankers = () => {
        const is_post = false;
        const url = 'dashboard/get-rank-holder';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setTopRankers(response.data);
            } else {
                setTopRankers([]);
            }
            setRankHolderLoader(false);
        })
    };

    // API - get data
    const _getOverviewData = () => {
        let is_post = false;
        let url = 'dashboard/get';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setOverviewData(response.data)
            }
            else {
                setOverviewData({})
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setLoader(false);
        })
    };


    // API - Cumulative frequency
    const _cumulativeFrequencyAum = () => {
        let is_post = true;
        let url = 'dashboard/aum';
        let request = {
            "filter_id": selectedOptionsAum.value
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setCumulativeFrequencyDataAum(response.data)
            } else {
                setCumulativeFrequencyDataAum({})
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }

            setGraphLoaderAum(false);
        })
    };

    const _cumulativeFrequencyUser = () => {
        let is_post = true;
        let url = 'dashboard/user';
        let request = {
            "filter_id": selectedOptionsUser.value
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setCumulativeFrequencyDataUser(response.data)
            } else {
                setCumulativeFrequencyDataUser({})
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setGraphLoaderUser(false);
        })
    };


    return (

        <Fragment>

            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <p className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Dashboard
                    </p>
                </div>
                {loader ?
                    <div className='d-flex align-items-center justify-content-center e-section-loader-dashboard'>
                        <img src={require("../../Assets/Image/loader.gif")}
                            draggable={false}
                            width={80}
                            height={80}
                            alt='loader'
                            className='text-center' />
                    </div>
                    :
                    <div className='padding-52px-lr margin--80px-top'>
                        {/* Order summary */}
                        <div className={`${style.e_order_summary_wrapper} e-bg-american-violet border-radius-16 padding-32px-all transition`}>

                            <h4 className='margin-24px-bottom e-text-white e-primary-font e-font-20 e-font-weight-600 line-height-24px'>
                                Overview
                            </h4>

                            <div className='d-flex align-items-center justify-content-start padding-40px-bottom gap-16px'>
                                <CustomTab data={TAB_TITLES}
                                    selectedTab={selectedTab}
                                    handleClick={_handleTab}
                                    customTab={true} />
                            </div>


                            {/* Statistic cards */}
                            {selectedTab === 1 ?
                                <div className='row row-cols-5 row-gap-4 '>
                                    <div className='col'>
                                        <StatisticCard title="Total AUM"
                                            value={overviewData.aum.total_aum_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="AUM in baskets"
                                            value={overviewData.aum.basket_aum_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="AUM in goals"
                                            value={overviewData.aum.goal_aum_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="AUM in gold"
                                            value={overviewData.aum.gold_aum_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="AUM in insta save"
                                            value={overviewData.aum.insta_save_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="AUM in other MF"
                                            value={overviewData.aum.other_aum_text} />
                                    </div>
                                    <div className='col'>
                                        <StatisticCard title="Average AUM/user"
                                            value={overviewData.aum.average_aum_text} />
                                    </div>
                                </div>
                                : selectedTab === 2 ?
                                    <div className='row row-cols-5 row-gap-4 '>
                                        <div className='col'>
                                            <StatisticCard title="Total transactions"
                                                value={overviewData.transactions.total_transactions} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Success transactions"
                                                value={overviewData.transactions.success_transactions} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Failed transactions"
                                                value={overviewData.transactions.failed_transactions} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Pending transactions"
                                                value={overviewData.transactions.pending_transactions} />
                                        </div>

                                    </div>
                                    :
                                    <div className='row row-cols-5 row-gap-4 '>
                                        <div className='col'>
                                            <StatisticCard title="Total users"
                                                value={overviewData.users.total_users} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Invested users"
                                                value={overviewData.users.invested_user} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Non-invested users"
                                                value={overviewData.users.non_invested_user} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Users invested in goals"
                                                value={overviewData.users.invested_goals} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Users invested in baskets"
                                                value={overviewData.users.invested_baskets} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Users invested in gold"
                                                value={overviewData.users.invested_gold} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Users invested in insta save"
                                                value={overviewData.users.invested_insta_save} />
                                        </div>
                                        <div className='col'>
                                            <StatisticCard title="Users invested in other MF"
                                                value={overviewData.users.invested_other_mf} />
                                        </div>
                                    </div>
                            }
                        </div>


                        {/* charts */}
                        <div className='row margin-32px-top'>
                            <div className='col-6 '>
                                <div className='e-bg-ghost-white padding-24px-all border-radius-16 e-chart-box-shadow d-flex flex-column h-100'>
                                    <div className='padding-26px-bottom d-flex align-items-center justify-content-between'>
                                        <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-20 e-font-weight-600 line-height-24px'>
                                            AUM growth
                                        </h3>

                                        <CustomSelectBox placeholder='Monthly'
                                            type={1}
                                            options={OPTIONS}
                                            value={selectedOptionsAum}
                                            onChange={_handleState} />
                                    </div>

                                    <div className='w-100 flex-grow-1'>
                                        {graphLoaderAum ?
                                            <div className='d-flex  align-items-center justify-content-center py-5 ' >
                                                <img src={require("../../Assets/Image/loader.gif")}
                                                    draggable={false}
                                                    width={80}
                                                    height={80}
                                                    alt='loader'
                                                    className='text-center' />
                                            </div>
                                            :
                                            <AreaChart data={cumulativeFrequencyDataAum.aum} />
                                        }
                                    </div>


                                </div>
                            </div>

                            <div className='col-6'>
                                <div className='e-bg-ghost-white padding-24px-all border-radius-16 e-chart-box-shadow d-flex flex-column h-100'>
                                    <div className='padding-26px-bottom d-flex align-items-center justify-content-between'>
                                        <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-20 e-font-weight-600 line-height-24px'>
                                            User growth
                                        </h3>
                                        <CustomSelectBox placeholder='Monthly'
                                            type={1}
                                            options={OPTIONS}
                                            value={selectedOptionsUser}
                                            onChange={_handleStateUser} />
                                    </div>
                                    {graphLoaderUser ?
                                        <div className='d-flex align-items-center justify-content-center py-5 '>
                                            <img src={require("../../Assets/Image/loader.gif")}
                                                draggable={false}
                                                width={80}
                                                height={80}
                                                alt='loader'
                                                className='text-center' />
                                        </div>
                                        :
                                        <div className='w-100 flex-grow-1'>
                                            <BarChart selectedOption={selectedOptionsUser.label}
                                                data={cumulativeFrequencyDataUser.users} />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>



                        {/* Order details */}
                        <div className={`${style.e_order_detail_table_wrapper} e-bg-ghost-white border-radius-16 margin-32px-top`}>
                            <div className='padding-32px-lr padding-32px-top d-flex align-items-center justify-content-between'>
                                <h4 className='mb-0 e-text-chinese-black e-primary-font e-font-20 e-font-weight-600 line-height-24px'>
                                    Top customers by highest investment
                                </h4>

                                <TextButton name='View all'
                                    icon={true}
                                    onClick={_handleViewAll} />

                            </div>
                            {/* Order list table */}
                            {customerAumLoader ?
                                <div className='d-flex align-items-center justify-content-center py-5'>
                                    <img src={require("../../Assets/Image/loader.gif")}
                                        draggable={false}
                                        width={80}
                                        height={80}
                                        alt='loader'
                                        className='text-center' />
                                </div>
                                :
                                <div className='e-data-table-wrapper-updated pt-4 padding-32px-bottom'>
                                    {customAumData.length !== 0 ?
                                        <CustomDataTable columns={ORDER_TABLE_COLUMN}
                                            data={customAumData}
                                            pagination={false}
                                            rowClick={(row) => _handleNavigate(row.customer_id)} />
                                        :
                                        <EmptyScreen width={80}
                                            height={80}
                                            padding="py-5"
                                            image={require('../../Assets/Image/NotFound/no-data.png')}
                                            title='No data found'
                                        />
                                    }
                                </div>
                            }
                        </div>


                        {/* Order details */}
                        <div className={`${style.e_order_detail_table_wrapper} e-bg-ghost-white border-radius-16 margin-32px-top`}>
                            <div className='padding-32px-lr padding-32px-top d-flex align-items-center justify-content-between'>
                                <h4 className='mb-0 e-text-chinese-black e-primary-font e-font-20 e-font-weight-600 line-height-24px'>
                                    Top rank holders
                                </h4>
                                <TextButton name="View all"
                                    icon={true}
                                    onClick={_handleViewAll} />

                            </div>
                            {/* Order list table */}
                            {rankHolderLoader ?
                                <div className='d-flex align-items-center justify-content-center min-vh-100'>
                                    <img src={require("../../Assets/Image/loader.gif")}
                                        draggable={false}
                                        width={80}
                                        height={80}
                                        alt='loader'
                                        className='text-center' />
                                </div>
                                :
                                <div className='e-data-table-wrapper-updated pt-4 padding-32px-bottom'>
                                    {topRankers.length !== 0 ?
                                        <CustomDataTable columns={RANK_TABLE_COLUMN}
                                            data={topRankers}
                                            pagination={false}
                                            rowClick={(row) => _handleNavigate(row.customer_id)} />
                                        :
                                        <EmptyScreen width={80}
                                            height={80}
                                            padding="py-5"
                                            image={require('../../Assets/Image/NotFound/no-data.png')}
                                            title='No data found'
                                        />
                                    }
                                </div>
                            }
                        </div>

                    </div>
                }

            </section>
        </Fragment >

    )
}
export default Dashboard;
